import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Monitor, Smartphone, Tablet, LogOut, ShieldCheck, RefreshCw, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/auth/sessions`;
const ICON = { phone: Smartphone, tablet: Tablet, desktop: Monitor };
const ago = (iso) => {
  if (!iso) return '';
  const s = Math.round((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 120) return 'active now';
  if (s < 3600) return `${Math.round(s / 60)} min ago`;
  if (s < 86400) return `${Math.round(s / 3600)} h ago`;
  return `${Math.round(s / 86400)} d ago`;
};

/** Settings → "Where you're signed in": every device with a live sign-in, with per-device sign out. */
export const ActiveSessions = ({ userId }) => {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const load = useCallback(() => axios.get(API, { params: userId ? { user_id: userId } : {} }).then(r => setData(r.data)).catch(() => setData({ sessions: [] })), [userId]);
  useEffect(() => { load(); }, [load]);

  const signOut = async (s) => {
    if (s.current && !window.confirm('Sign out this device (the one you are using now)?')) return;
    setBusy(s.sid);
    try {
      await axios.delete(`${API}/${s.sid}`);
      toast.success(s.current ? 'Signed out' : `Signed out ${s.device?.label || 'that device'}`);
      if (s.current) { localStorage.removeItem('token'); window.location.href = '/login'; return; }
      load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not sign out that device'); }
    setBusy('');
  };
  const signOutOthers = async () => {
    if (!window.confirm('Sign out every other device? Only this one stays signed in.')) return;
    setBusy('others');
    try { const r = await axios.post(`${API}/sign-out-others`, {}); toast.success(`Signed out ${r.data.signed_out} other device${r.data.signed_out === 1 ? '' : 's'}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || 'Could not sign out'); }
    setBusy('');
  };

  if (!data) return <p className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" />Checking your devices…</p>;
  const others = data.sessions.filter(s => !s.current);
  return (
    <div data-testid="active-sessions">
      <div className="flex items-center justify-between gap-2 mb-3">
        <p className="text-sm text-slate-600">Every place {userId ? 'this account is' : "you're"} signed in. Sign out anything you don't recognise — it stops working within seconds.</p>
        <button onClick={load} className="p-1.5 rounded-lg text-slate-500 hover:bg-slate-100" title="Refresh" data-testid="sessions-refresh"><RefreshCw className="w-4 h-4" /></button>
      </div>
      {data.legacy_token && !userId && (
        <p className="text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="sessions-legacy-note">This device signed in before device tracking existed, so it isn't in the list yet — sign out and back in once and it will show up.</p>
      )}
      <ul className="divide-y divide-slate-200 rounded-xl border border-slate-200 bg-white overflow-hidden">
        {data.sessions.map(s => { const Icon = ICON[s.device?.type] || Monitor; return (
          <li key={s.sid} className={`flex items-center gap-3 px-4 py-3 ${s.current ? 'bg-sky-50/70' : ''}`} data-testid={`session-${s.sid}`}>
            <div className={`w-9 h-9 rounded-full flex items-center justify-center shrink-0 ${s.current ? 'bg-sky-600 text-white' : 'bg-slate-100 text-slate-600'}`}><Icon className="w-4 h-4" /></div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-900 truncate">{s.device?.label || 'Unknown device'}{s.device?.pwa ? ' · app' : ''}
                {s.current && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-600 text-white" data-testid="session-current-badge">This device</span>}
                {!s.current && s.same_network && <span className="ml-2 text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-800">Same network as you</span>}
              </p>
              <p className="text-xs text-slate-500 truncate">{ago(s.last_seen)} · IP {s.ip || '—'} · signed in {new Date(s.created_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' })} {new Date(s.created_at).toLocaleTimeString('en-CA', { hour: 'numeric', minute: '2-digit' })}</p>
            </div>
            <button onClick={() => signOut(s)} disabled={busy === s.sid} className="px-3 h-8 rounded-lg border border-slate-200 bg-white hover:border-red-300 hover:text-red-700 text-xs font-semibold text-slate-700 inline-flex items-center gap-1.5 disabled:opacity-50 shrink-0" data-testid={`session-signout-${s.sid}`}>
              {busy === s.sid ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <LogOut className="w-3.5 h-3.5" />}Sign out
            </button>
          </li>); })}
        {!data.sessions.length && <li className="px-4 py-6 text-sm text-slate-500 text-center" data-testid="sessions-empty">No tracked sign-ins yet.</li>}
      </ul>
      {others.length > 0 && !userId && (
        <button onClick={signOutOthers} disabled={busy === 'others'} className="mt-3 px-4 h-9 rounded-lg bg-slate-900 hover:bg-red-700 text-white text-sm font-semibold inline-flex items-center gap-2 disabled:opacity-50" data-testid="sessions-signout-others">
          {busy === 'others' ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />}Sign out all other devices ({others.length})
        </button>
      )}
    </div>
  );
};
